/**
 * 요약·의도 해석 전용 드로어 — §14.2.
 *
 * ■ 왜 코치 피드에서 뺐나
 *   코치 항목은 회의 내내 훑는 **짧은 한 줄**(제안·신호·요지·불일치·미답)이고,
 *   요약/의도 해석은 한 번 읽는 **긴 문서**다. 둘을 한 스크롤에 쌓으면 문서가
 *   항상 피드를 묻는다 — 실사용에서 요약 1건이 코치 18건을 화면 밖으로 밀어냈다.
 *   성격이 다른 것을 같은 목록에 넣은 것이 원인이라, 표면을 나눈다.
 *
 * ■ 다시 열 수 있어야 한다
 *   결과는 모델 호출 비용이 든다. 한 번 보고 사라지면 재실행을 강요하게 되므로,
 *   회의 동안 받은 결과를 시각과 함께 목록으로 남기고 언제든 다시 연다.
 */

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@cupia/ui/drawer'
import { Badge } from '@cupia/ui/badge'
import { Button } from '@cupia/ui/button'
import { Separator } from '@cupia/ui/separator'
import { Spinner } from '@cupia/ui/spinner'

/** 한 번의 요청 결과. `body` 가 비고 `error` 가 있으면 실패다. */
export type SummaryEntry = {
  id: string
  kind: 'summary' | 'intent'
  body: string
  ts: number
  /** 실패 사유(있으면 실패 항목) */
  error?: string
}

export const kindLabel = (k: 'summary' | 'intent') =>
  k === 'summary' ? '요약' : '의도 해석'

const hhmm = (ts: number) => {
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 최신순 목록(호출자가 정렬해 넘긴다). */
  entries: SummaryEntry[]
  /** 현재 보고 있는 항목 id — 없으면 최신을 본다. */
  selectedId?: string
  onSelect: (id: string) => void
  /** 요청이 진행 중이면 그 종류. */
  pending?: 'summary' | 'intent' | null
}

export function SummaryDrawer({
  open,
  onOpenChange,
  entries,
  selectedId,
  onSelect,
  pending,
}: Props) {
  const current = entries.find((e) => e.id === selectedId) ?? entries[0]

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className="max-h-[85vh]"
        data-testid="summary-drawer"
      >
        <DrawerHeader>
          <DrawerTitle>요약 · 의도 해석</DrawerTitle>
          <DrawerDescription>
            회의 중 요청한 결과입니다. 코치 피드와 분리돼 있어 길어져도 코치
            항목을 가리지 않습니다.
          </DrawerDescription>
        </DrawerHeader>

        {/* 지난 결과 목록 — 여러 번 요청했으면 시각으로 고른다. */}
        {entries.length > 1 && (
          <div
            className="flex flex-wrap gap-1.5 px-4 pb-2"
            data-testid="summary-history"
          >
            {entries.map((e) => (
              <Button
                key={e.id}
                size="sm"
                variant={e.id === current?.id ? 'secondary' : 'ghost'}
                onClick={() => onSelect(e.id)}
                data-testid="summary-history-item"
              >
                {kindLabel(e.kind)} {hhmm(e.ts)}
              </Button>
            ))}
          </div>
        )}

        <Separator />

        {/* 본문 — **자체 스크롤 영역**이라 아무리 길어도 다른 화면에 영향 없다. */}
        <div
          className="min-h-0 flex-1 overflow-y-auto px-4 py-3"
          data-testid="summary-body"
        >
          {pending && !current && (
            <div className="flex items-center gap-2 text-(length:--text-sm-size) text-muted-foreground">
              <Spinner className="size-4" />
              {kindLabel(pending)}을 만들고 있습니다…
            </div>
          )}

          {!pending && !current && (
            <p className="text-(length:--text-sm-size) text-muted-foreground">
              아직 요청한 결과가 없습니다. 상단의 <b>요약</b> 또는{' '}
              <b>의도 해석</b> 을 눌러 보세요.
            </p>
          )}

          {current && (
            <>
              <div className="mb-2 flex items-center gap-2">
                <Badge variant="soft">{kindLabel(current.kind)}</Badge>
                <span className="text-(length:--text-xs-size) text-muted-foreground">
                  {hhmm(current.ts)}
                </span>
                {pending && (
                  <span className="ml-auto flex items-center gap-1.5 text-(length:--text-xs-size) text-muted-foreground">
                    <Spinner className="size-3" />
                    {kindLabel(pending)} 요청 중
                  </span>
                )}
              </div>
              {current.error ? (
                <p
                  className="text-(length:--text-sm-size) text-destructive"
                  data-testid="summary-error"
                >
                  {current.error}
                </p>
              ) : (
                <pre className="whitespace-pre-wrap font-sans text-(length:--text-sm-size) leading-relaxed">
                  {current.body}
                </pre>
              )}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
